// Public Influencer Profile - InfluenceHub Kit layout
// Single scrollable layout with neutral skeleton styling

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext.jsx';
import { getUserRole } from '../../lib/getUserRole';
import { useInfluencerProfile } from '../../hooks/useInfluencerProfile';
import { isEmptyValue, hasAnyData, hasAnyArrayData, shouldShowPlatformPerformance } from '../../utils/profileVisibility';
import IdentitySection from '../../components/profile/IdentitySection';
import ProfileBioSectionBeacons from '../../components/profile/ProfileBioSectionBeacons';
import PlatformPerformanceSection from '../../components/profile/PlatformPerformanceSection';
import AudienceSection from '../../components/profile/AudienceSection';
import BrandPartnersSection from '../../components/profile/BrandPartnersSection';
import CollaborationHighlightsSection from '../../components/profile/CollaborationHighlightsSection';
import FeaturedPostsSection from '../../components/profile/FeaturedPostsSection';
import { normalizeFeaturedPosts } from '../../lib/featuredPosts/normalizeFeaturedPosts';
import { ensureTikTokThumbnailsCached } from '../../lib/featuredPosts/ensureTikTokThumbnailsCached';
import { event as trackEvent } from '../../analytics/ga';
import { db } from '../../lib/firebase';
import { getCanonicalSocials, getSocialsForProfileDisplay } from '../../lib/socials';
import { getPlatformsFromProfile } from '../../lib/social/utils/socialLinks';
import BrandCTAAside from '../../components/profile/BrandCTAAside';
import { canonicalizeMisroutedFeedbackPath } from '../../lib/routing/canonicalizeMisroutedFeedbackPath.js';

function getUsernameFromPath() {
  if (typeof window === 'undefined') return '';
  const path = window.location.pathname;
  if (!path.startsWith('/u/')) return '';
  const parts = path.split('/').filter(Boolean);
  const segment = parts[1];
  return segment && segment.trim() && segment !== 'u' ? segment.trim() : '';
}

const PublicProfile = ({ initialUsername, onNavigate, skipAppHeader }) => {
  const [username, setUsername] = useState(() => {
    const fromPath = getUsernameFromPath();
    if (fromPath) return fromPath;
    const fromProp = typeof initialUsername === 'string' && initialUsername.trim() && initialUsername.trim() !== 'u' ? initialUsername.trim() : '';
    return fromProp || '';
  });
  const [userDoc, setUserDoc] = useState(null);
  const [viewerRole, setViewerRole] = useState('guest');
  const { user } = useAuth();

  useEffect(() => {
    try {
      const path = window.location.pathname;
      if (!path.startsWith('/u/')) {
        if (initialUsername && initialUsername.trim() && initialUsername.trim() !== 'u') {
          setUsername(initialUsername.trim());
        }
        return;
      }
      const parts = path.split('/').filter(Boolean);
      const pathUsername = parts[1] && parts[1].trim() && parts[1] !== 'u' ? parts[1].trim() : null;
      if (pathUsername) {
        setUsername(pathUsername);
      } else if (initialUsername && initialUsername.trim() && initialUsername.trim() !== 'u') {
        setUsername(initialUsername.trim());
      }
    } catch (err) {
      console.error('[PublicProfile] Error extracting username:', err);
    }
  }, [initialUsername]);

  // Safety net: /u/feedback → /feedback if something bypassed inline head script + App canonicalize
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const p = window.location.pathname.replace(/\/$/, '') || '/';
    if (p === '/u/feedback' || p.startsWith('/u/feedback/')) {
      canonicalizeMisroutedFeedbackPath();
      window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
    }
  }, []);

  const effectiveUsername = typeof username === 'string' && username.trim() && username.trim() !== 'u' ? username.trim() : '';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (path.startsWith('/u/')) {
      console.log('[PublicProfile] route param (debug):', { path, passedToHook: effectiveUsername || '(none)' });
    }
  }, [effectiveUsername]);

  const {
    profile: influencerProfile,
    loading,
    error,
    isOwner,
    updateProfile,
    updateIdentity,
    updatePlatformStats,
    updateAudience,
    updateBrandPartners,
    updateCollabHighlights,
    updateFeaturedPosts,
    updateReliability,
  } = useInfluencerProfile(effectiveUsername);

  useEffect(() => {
    if (!influencerProfile?.userId) return;
    let isMounted = true;
    const loadUserDoc = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', influencerProfile.userId));
        if (!isMounted) return;
        setUserDoc(snap.exists() ? (snap.data() || {}) : null);
      } catch (err) {
        console.warn('[PublicProfile] Failed to load users doc:', err);
      }
    };
    loadUserDoc();
    return () => {
      isMounted = false;
    };
  }, [influencerProfile?.userId]);

  useEffect(() => {
    if (!user?.uid) {
      setViewerRole('guest');
      return;
    }
    let isMounted = true;
    getUserRole(user.uid).then((data) => {
      if (!isMounted) return;
      const role = data?.role;
      if (role === 'brand') setViewerRole('brand');
      else if (role === 'creator' || role === 'influencer') setViewerRole('creator');
      else setViewerRole('guest');
    });
    return () => { isMounted = false; };
  }, [user?.uid]);

  // Normalize featured posts for backward compatibility
  const rawFeaturedPosts = influencerProfile?.featuredPosts;
  const featuredPosts = normalizeFeaturedPosts(rawFeaturedPosts);

  // Backfill thumbnails for legacy posts (rate limited, runs in background)
  // IMPORTANT: this hook must be declared before any early returns to avoid
  // changing the hooks order between renders (React error #310).
  useEffect(() => {
    if (!influencerProfile?.id) return;
    if (featuredPosts.length === 0) return;

    ensureTikTokThumbnailsCached(influencerProfile.id, featuredPosts).catch((err) => {
      console.warn('[PublicProfile] Failed to backfill thumbnails:', err);
    });
  }, [influencerProfile?.id, featuredPosts.length]);

  // Debug: Log isOwner value to verify it's working correctly
  useEffect(() => {
    console.log('[PublicProfile] isOwner check:', {
      isOwner,
      hasProfile: !!influencerProfile,
      profileUserId: influencerProfile?.userId,
    });
  }, [isOwner, influencerProfile?.userId]);

  // Debug logging - MUST be before any early returns
  useEffect(() => {
    console.log('[PublicProfile] Render state:', { 
      username, 
      loading, 
      error, 
      hasProfile: !!influencerProfile,
      profileKeys: influencerProfile ? Object.keys(influencerProfile) : [],
      profileId: influencerProfile?.id,
      profileUserId: influencerProfile?.userId,
    });
  }, [username, loading, error, influencerProfile]);

  // Debug logging for profile data structure - MUST be before any early returns
  useEffect(() => {
    if (influencerProfile) {
      console.log('[PublicProfile] ========== FULL PROFILE DATA ==========');
      console.log('[PublicProfile] Full profile object:', JSON.parse(JSON.stringify(influencerProfile)));
      console.log('[PublicProfile] All top-level keys:', Object.keys(influencerProfile));
      console.log('[PublicProfile] Identity object:', JSON.parse(JSON.stringify(influencerProfile.identity || {})));
      console.log('[PublicProfile] Display name:', {
        topLevel: influencerProfile.displayName,
        identityLevel: influencerProfile.identity?.displayName,
        final: influencerProfile.displayName || influencerProfile.identity?.displayName,
      });
      console.log('[PublicProfile] Username:', {
        topLevel: influencerProfile.username,
        identityHandle: influencerProfile.identity?.handle,
        final: influencerProfile.username || influencerProfile.identity?.handle,
      });
      console.log('[PublicProfile] Avatar URLs:', {
        photoURL: influencerProfile.photoURL,
        profileImageUrl: influencerProfile.profileImageUrl,
        avatarUrl: influencerProfile.avatarUrl,
        identityPhotoURL: influencerProfile.identity?.photoURL,
        identityProfileImageUrl: influencerProfile.identity?.profileImageUrl,
        identityAvatarUrl: influencerProfile.identity?.avatarUrl,
        finalAvatarUrl: influencerProfile.photoURL || influencerProfile.profileImageUrl || influencerProfile.avatarUrl,
      });
      console.log('[PublicProfile] Bio:', influencerProfile.bio);
      console.log('[PublicProfile] Platform stats:', JSON.parse(JSON.stringify(influencerProfile.platformStats || {})));
      console.log('[PublicProfile] Location:', {
        topLevel: influencerProfile.location,
        identityLevel: influencerProfile.identity?.location,
      });
      console.log('[PublicProfile] Contact email:', influencerProfile.contactEmail);
      console.log('[PublicProfile] Niche:', influencerProfile.niche);
      console.log('[PublicProfile] Creator title:', {
        topLevel: influencerProfile.creatorTitle,
        identityLevel: influencerProfile.identity?.creatorTitle,
      });
      console.log('[PublicProfile] Social links:', JSON.parse(JSON.stringify(influencerProfile.socialLinks || {})));
      console.log('[PublicProfile] Banner image URL:', influencerProfile.bannerImageUrl);
      console.log('[PublicProfile] =======================================');
    } else if (!loading && !error) {
      console.warn('[PublicProfile] No profile loaded but not loading and no error');
    }
  }, [influencerProfile, loading, error]);

  // Analytics: Track profile_viewed event when profile is successfully loaded
  // Event triggered: profile_viewed - when a public profile page is viewed (non-owner)
  // Use ref to prevent duplicate firing on re-renders
  const profileViewedFired = useRef(null);
  useEffect(() => {
    const currentProfileId = influencerProfile?.id;
    // Reset ref if profile changes (different profile viewed)
    if (currentProfileId && profileViewedFired.current !== currentProfileId) {
      profileViewedFired.current = null;
    }
    // Fire event if profile is loaded, not owner, and hasn't fired for this profile
    if (!loading && !error && currentProfileId && !isOwner && profileViewedFired.current !== currentProfileId) {
      profileViewedFired.current = currentProfileId;
      try {
        // Only send profile_id (non-PII) - do not send username, email, or other PII
        trackEvent('profile_viewed', {
          profile_id: currentProfileId,
        });
      } catch (err) {
        // Analytics must never block page rendering
        console.warn('[PublicProfile] Analytics error:', err);
      }
    }
  }, [loading, error, influencerProfile?.id, isOwner]);

  // Set page metadata for SEO - MUST be before early returns
  useEffect(() => {
    if (influencerProfile) {
      // Use same identity construction logic as main render
      const identityForSEO = {
        ...(influencerProfile?.identity || {}),
        displayName: influencerProfile?.identity?.displayName || influencerProfile?.displayName || '',
        handle: influencerProfile?.identity?.handle || influencerProfile?.username || '',
      };
      const profileName = identityForSEO.displayName || identityForSEO.handle || username || 'Creator';
      const title = `${profileName} | InfluenceHub`;
      const description = `View ${profileName}'s creator media kit — stats, social links, content, and brand collaboration highlights.`;
      
      document.title = title;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
    } else if (!loading && !error) {
      // Fallback if no profile
      document.title = 'Creator Media Kit | InfluenceHub';
    }
  }, [influencerProfile, username, loading, error]);

  // Build identity object by merging identity object with top-level fields (for backward compatibility)
  // This matches how the profile editor loads data
  // Use useMemo to prevent recreating the object on every render (fixes hooks error)
  // MUST be before early returns
  const identity = useMemo(() => {
    if (!influencerProfile) return {};
    
    return {
      // Start with identity object if it exists
      ...(influencerProfile?.identity || {}),
      // Override with top-level fields if identity object is missing those fields
      displayName: influencerProfile?.identity?.displayName || influencerProfile?.displayName || '',
      handle: influencerProfile?.identity?.handle || influencerProfile?.username || '',
      creatorTitle: influencerProfile?.identity?.creatorTitle || influencerProfile?.creatorTitle || '',
      subtitle: influencerProfile?.identity?.subtitle || (Array.isArray(influencerProfile?.niche) ? influencerProfile.niche.join(', ') : (influencerProfile?.niche || '')),
      location: influencerProfile?.identity?.location || influencerProfile?.location || '',
      primaryPlatforms: influencerProfile?.identity?.primaryPlatforms || influencerProfile?.platforms?.map(p => p.platformName) || [],
    };
  }, [
    influencerProfile?.identity,
    influencerProfile?.displayName,
    influencerProfile?.username,
    influencerProfile?.creatorTitle,
    influencerProfile?.niche,
    influencerProfile?.location,
    influencerProfile?.platforms,
  ]);

  const canonicalSocials = useMemo(() => {
    if (!influencerProfile) return [];
    return getSocialsForProfileDisplay(influencerProfile, userDoc);
  }, [influencerProfile, userDoc]);

  // Early returns - MUST come after all hooks
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-700">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !influencerProfile) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Profile Not Found</h1>
          <p className="text-slate-700 mb-6">
            {error || 'The profile you\'re looking for doesn\'t exist or has been removed.'}
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  // Safe access to profile data with defaults
  const platformStats = influencerProfile?.platformStats || {};
  const socialAccounts = influencerProfile?.socialAccounts || {};
  const audience = influencerProfile?.audience || {};
  const brandPartners = influencerProfile?.brandPartners || [];
  const collaborations = influencerProfile?.collaborations || [];

  const reliability = influencerProfile?.reliability || {};
  // Get creatorTitle from identity or top-level (for backward compatibility)
  const creatorTitle = identity?.creatorTitle || influencerProfile?.creatorTitle || '';
  
  // Helper function to check if audience has meaningful data
  // This matches the exact logic used in AudienceSection component
  const hasAudienceData = (aud) => {
    if (!aud || typeof aud !== 'object') return false;
    
    const gender = aud.gender || {};
    const ages = aud.ageBrackets || {};
    
    // Check gender data - must have at least one value > 0 (matches AudienceSection filter)
    const genderData = [
      { value: gender.femalePercent || 0 },
      { value: gender.malePercent || 0 },
      { value: gender.otherPercent || 0 },
    ].filter((item) => item.value > 0);
    const hasGender = genderData.length > 0;
    
    // Check age brackets - must have at least one value > 0 (matches AudienceSection filter)
    const ageData = [
      { value: ages['13-17'] || 0 },
      { value: ages['18-24'] || 0 },
      { value: ages['25-34'] || 0 },
      { value: ages['35+'] || 0 },
    ].filter((item) => item.value > 0);
    const hasAges = ageData.length > 0;
    
    // Check top countries - must have at least one with a name (matches AudienceSection filter)
    const rankedCountries = (aud.topCountries || [])
      .filter((item) => item && item.name)
      .sort((a, b) => (b.percent ?? 0) - (a.percent ?? 0))
      .slice(0, 3);
    const hasCountries = rankedCountries.length > 0;
    
    // Check primary language (matches AudienceSection check)
    const hasLanguage = !isEmptyValue(aud.primaryLanguage);
    
    // Return true if any of these have data (matches AudienceSection hasAnyData logic)
    return hasGender || hasAges || hasCountries || hasLanguage;
  };

  // Compute hasData map - check if sections actually have meaningful data
  const hasPlatformStats = platformStats && Object.keys(platformStats).length > 0 && 
    Object.values(platformStats).some((stats) => {
      if (!stats || typeof stats !== 'object') return false;
      return (
        (stats.followers != null && stats.followers !== '') ||
        (stats.subscribers != null && stats.subscribers !== '') ||
        (stats.avgViews != null && stats.avgViews !== '') ||
        (stats.avgViews30Days != null && stats.avgViews30Days !== '') ||
        (stats.views30Days != null && stats.views30Days !== '') ||
        (stats.totalViews != null && stats.totalViews !== '')
      );
    });
  const hasSocialAccountsTiktok = socialAccounts?.tiktok?.derived && (
    socialAccounts.tiktok.followersCount != null || socialAccounts.tiktok.derived.avgViews10 != null
  );
  const hasSocialAccountsInstagram =
    socialAccounts?.instagram &&
    (socialAccounts.instagram.followersCount != null ||
      (socialAccounts.instagram.derived && (
        socialAccounts.instagram.derived.medianViews10 != null ||
        socialAccounts.instagram.derived.medianEngagement10 != null ||
        socialAccounts.instagram.derived.erFollowersMedian != null
      )));
  const isViewingCreatorProfile = !!influencerProfile;
  const showGuestNav = viewerRole === 'guest';
  const showBottomBanner = viewerRole === 'guest';
  const showBrandCTA = viewerRole === 'brand' && !isOwner;

  const hasData = {
    platformPerformance: hasPlatformStats || hasSocialAccountsTiktok || hasSocialAccountsInstagram,
    audience: hasAudienceData(audience),
    brandPartners: Array.isArray(brandPartners) && brandPartners.length > 0 && 
      brandPartners.some((partner) => partner && partner.name && partner.name.trim()),
    collaborations: Array.isArray(collaborations) && collaborations.length > 0 && 
      collaborations.some((collab) => collab && (
        (collab.brandName && collab.brandName.trim()) ||
        (collab.description && collab.description.trim()) ||
        (collab.imageUrl && collab.imageUrl.trim())
      )),
    featuredPosts: featuredPosts.length > 0 && 
      featuredPosts.some((post) => post && (
        (post.title && post.title.trim()) ||
        (post.thumbnailUrl && post.thumbnailUrl.trim()) ||
        (post.url && post.url.trim())
      )),
  };

  // Get sectionsEnabled from profile
  const sectionsEnabled = influencerProfile?.sectionsEnabled || {};

  // Visibility logic for PUBLIC profile:
  // Sections show ONLY if:
  // 1. They have actual data (hasData)
  // 2. AND the creator has enabled them (sectionsEnabled)
  // Platform Performance is default-on: show unless explicitly set to false
  const showPlatformPerformance = hasData.platformPerformance && shouldShowPlatformPerformance(sectionsEnabled);
  const showAudience = hasData.audience && sectionsEnabled.audience === true;
  const showBrandPartners = hasData.brandPartners && sectionsEnabled.brandPartners === true;
  const showCollaborations = hasData.collaborations && sectionsEnabled.collaborations === true;
  const showFeaturedPosts = hasData.featuredPosts && sectionsEnabled.featuredPosts === true;

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-viewer-role={viewerRole} data-is-viewing-creator-profile={isViewingCreatorProfile}>
      {!skipAppHeader && (
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50 flex-shrink-0 no-print">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <a
                href="/"
                className="flex items-center gap-2 text-lg sm:text-xl font-semibold text-slate-900 tracking-tight"
              >
                <img
                  src="/LogoSquare.png"
                  alt="InfluenceHub"
                  className="h-6 w-6 sm:h-7 sm:w-7 rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <span>InfluenceHub</span>
              </a>
              <div className="flex items-center gap-3">
                {showGuestNav ? (
                  <>
                    <a
                      href="/login"
                      className="text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                    >
                      Log in
                    </a>
                    <a
                      href="/signup"
                      className="inline-flex items-center rounded-full bg-blue-600 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                    >
                      Create your media kit
                    </a>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => onNavigate(viewerRole === 'brand' ? 'brand-dashboard' : 'profile-edit', null, { replace: false })}
                    className="text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    Dashboard
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Profile Layout - main column; optional right rail for brand */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex flex-col lg:flex-row lg:gap-8 max-w-6xl mx-auto w-full px-3 sm:px-4 py-5 sm:py-6 md:py-8">
          <main className="order-2 lg:order-1 flex-1 min-w-0 space-y-5 sm:space-y-6 md:space-y-8">
            {/* 1️⃣ Identity Snapshot */}
            {influencerProfile && (() => {
              // Compute avatar URL with comprehensive fallback
              const computedAvatarUrl = influencerProfile.photoURL || 
                                        influencerProfile.profileImageUrl || 
                                        influencerProfile.avatarUrl ||
                                        influencerProfile.identity?.photoURL ||
                                        influencerProfile.identity?.profileImageUrl ||
                                        influencerProfile.identity?.avatarUrl ||
                                        '';
              
              console.log('[PublicProfile] Rendering IdentitySection with:', {
                computedAvatarUrl,
                photoURL: influencerProfile.photoURL,
                profileImageUrl: influencerProfile.profileImageUrl,
                avatarUrl: influencerProfile.avatarUrl,
                identityPhotoURL: influencerProfile.identity?.photoURL,
                identityProfileImageUrl: influencerProfile.identity?.profileImageUrl,
                identityAvatarUrl: influencerProfile.identity?.avatarUrl,
                identityDisplayName: identity.displayName,
                identityHandle: identity.handle,
                contactEmail: influencerProfile.contactEmail,
                hasSocialLinks: !!influencerProfile.socialLinks && Object.keys(influencerProfile.socialLinks).length > 0,
              });
              
              return (
                <IdentitySection
                  identity={{
                    ...identity,
                    creatorTitle: creatorTitle, // Pass creatorTitle to IdentitySection
                    niches: influencerProfile?.niches || (influencerProfile?.niche ? (Array.isArray(influencerProfile.niche) ? influencerProfile.niche : [influencerProfile.niche]) : []),
                  }}
                  platformStats={platformStats}
                  socialAccounts={socialAccounts}
                  isOwner={isOwner}
                  showTotalFollowers={showPlatformPerformance} // Hide Total Followers if Platform Performance is disabled
                  avatarUrl={computedAvatarUrl}
                  bannerImageUrl={influencerProfile.bannerImageUrl || ''}
                  contactEmail={influencerProfile.contactEmail || ''}
                  socialLinks={influencerProfile.socialLinks || {}}
                  username={username}
                  socials={canonicalSocials}
                  onSave={async (partialIdentity) => {
                    try {
                      await updateIdentity(partialIdentity);
                    } catch (err) {
                      console.error('Error updating identity:', err);
                    }
                  }}
                />
              );
            })()}

            {/* 2️⃣ About Me */}
            {influencerProfile && (
              <ProfileBioSectionBeacons
                profile={influencerProfile}
                isOwner={isOwner}
                onBioChange={async (bio) => {
                  try {
                    await updateProfile({ bio });
                  } catch (err) {
                    console.error('Error updating bio:', err);
                  }
                }}
              />
            )}

            {/* 3️⃣ Platform Performance */}
            {influencerProfile && showPlatformPerformance && (() => {
              const normalizedPlatforms = getPlatformsFromProfile({
                socialLinks: influencerProfile?.socialLinks,
                socialAccounts: influencerProfile?.socialAccounts,
              });

              return (
                <PlatformPerformanceSection
                  platformStats={platformStats}
                  primaryPlatforms={normalizedPlatforms}
isOwner={isOwner}
                mode="public"
                  socialAccounts={socialAccounts}
                  socialLinks={influencerProfile?.socialLinks}
                  onSavePlatformStats={async (stats) => {
                    try {
                      await updatePlatformStats(stats);
                    } catch (err) {
                      console.error('Error updating platform stats:', err);
                    }
                  }}
                />
              );
            })()}

            {/* 4️⃣ Audience & Demographics */}
            {influencerProfile && showAudience && (
              <AudienceSection
                audience={audience}
                isOwner={isOwner}
                mode="public"
                onSaveAudience={async (partialAudience) => {
                  try {
                    await updateAudience(partialAudience);
                  } catch (err) {
                    console.error('Error updating audience:', err);
                  }
                }}
              />
            )}

            {/* 5️⃣ Brand Partners */}
            {influencerProfile && showBrandPartners && (
              <BrandPartnersSection
                brandPartners={brandPartners}
                isOwner={isOwner}
                mode="public"
                onSaveBrandPartners={async (partners) => {
                  try {
                    await updateBrandPartners(partners);
                  } catch (err) {
                    console.error('Error updating brand partners:', err);
                  }
                }}
              />
            )}

            {/* 6️⃣ Collaboration Highlights */}
            {influencerProfile && showCollaborations && (
              <CollaborationHighlightsSection
                collaborations={collaborations}
                isOwner={isOwner}
                mode="public"
                onSaveCollaborations={async (collabs) => {
                  try {
                    await updateProfile({ collaborations: collabs });
                  } catch (err) {
                    console.error('Error updating collaborations:', err);
                  }
                }}
              />
            )}

            {/* 7️⃣ Featured Posts */}
            {influencerProfile && showFeaturedPosts && (
              <FeaturedPostsSection
                featuredPosts={featuredPosts}
                profile={influencerProfile}
                isOwner={isOwner}
                mode="public"
                onSaveFeaturedPosts={async (posts) => {
                  try {
                    await updateProfile({ featuredPosts: posts });
                  } catch (err) {
                    console.error('Error updating featured posts:', err);
                  }
                }}
              />
            )}

            {/* 8️⃣ Bottom conversion banner - guests only */}
            {showBottomBanner && (
              <section className="mt-10 no-print">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-6 md:px-8 md:py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      Want a media kit like this?
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Create your own InfluenceHub media kit and share one clean link with brands, managers, and collaborators.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href="/signup"
                      className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors"
                    >
                      Create your media kit
                    </a>
                    <a
                      href="/login"
                      className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                    >
                      Log in
                    </a>
                  </div>
                </div>
              </section>
            )}
          </main>
          {showBrandCTA && (
            <div className="order-1 lg:order-2 flex-shrink-0">
              <BrandCTAAside />
            </div>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1.5cm;
            size: A4;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          * {
            color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          img {
            max-width: 100%;
            height: auto;
            page-break-inside: avoid;
          }
          section {
            page-break-inside: avoid;
          }
          /* Ensure banner and avatar print well */
          .aspect-\\[4\\/1\\] {
            page-break-inside: avoid;
          }
          /* Reduce shadows in print */
          .shadow-md,
          .shadow-lg,
          .shadow-sm {
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PublicProfile;
